/**
 * Motor de cuotas: el calendario de una compra diferida.
 *
 * ── LA PREGUNTA QUE ESTE ARCHIVO EXISTE PARA RESPONDER ──────────────────────
 *
 * "Compré un bolso en $465.000 a 3 cuotas. ¿Cuándo pago, cuánto pago cada vez,
 * y cuánto me cuesta de más por no haberlo pagado de una?"
 *
 * Una app que solo guarda "debo $465.000" no responde ninguna de las tres.
 * Por eso aquí no se guarda un saldo: se genera el CALENDARIO COMPLETO en el
 * momento de la compra, cuota por cuota, con su fecha y su plata. Después,
 * pagar es tachar renglones de ese calendario.
 *
 * ── LAS DOS FORMAS DE DIFERIR, QUE NO SE PAGAN IGUAL ────────────────────────
 *
 *   TARJETA DE CRÉDITO (Nu, Visa)
 *     La cuota entra en la factura del periodo. La fecha depende de la fecha
 *     de corte: comprar el 14 con corte el 15 te da 11 días para pagar;
 *     comprar el 16, casi 41. Es la misma compra y el mismo precio.
 *
 *   CRÉDITO POR COMPRA (Addi, Sistecrédito)
 *     Cada compra es su propio crédito con su propio calendario, contado
 *     desde el día que compraste. No hay fecha de corte que valga.
 *
 * ── LOS DOS SISTEMAS DE INTERÉS, QUE TAMPOCO SE PAGAN IGUAL ─────────────────
 *
 *   FRANCÉS (cuota fija) — los créditos de consumo, como el de Nequi.
 *     Todas las cuotas valen lo mismo; adentro cambia la mezcla.
 *
 *   CAPITAL FIJO — las tarjetas de crédito en Colombia, como Nu.
 *     Cada mes se paga la misma parte de la compra (compra ÷ cuotas) más los
 *     intereses de lo que se sigue debiendo. La primera cuota es la más cara
 *     y bajan. Lo prueba la app de Nu de Esteban: su "pago mínimo estimado"
 *     de $108.048,87 es EXACTO la suma de cada compra de Temu entre sus
 *     cuotas. Los intereses llegan aparte, al corte.
 *
 * ── POR QUÉ LA ÚLTIMA CUOTA NO ES IGUAL A LAS OTRAS ─────────────────────────
 *
 * Porque los pesos no se parten. $465.000 entre 3 da exacto, pero $100.000
 * entre 3 da $33.333,33. Si se redondea cada cuota, la suma no da $100.000 y
 * queda un peso bailando. Aquí la última cuota absorbe el residuo: el plan
 * SIEMPRE suma exactamente lo que se debe. Hay una prueba que lo verifica
 * para todos los montos de $1 a $100.000 y para 2, 3, 6, 12, 24 y 36 cuotas.
 */

import { repartir } from './dinero.js';
import { eaAMv } from './parametros.js';

const DIA = 86400000;

// ─── Fechas ─────────────────────────────────────────────────────────────────

function iso(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function hoyISO() {
  return iso(new Date());
}

/**
 * Suma meses respetando el fin de mes.
 *
 * El 31 de enero más un mes no es el 31 de febrero: es el 28 (o el 29).
 * Sin esta guarda, `new Date(2026, 1, 31)` se desborda solo al 3 de marzo y
 * la cuota queda con fecha de otro mes. Los bancos hacen exactamente esto:
 * corren la fecha al último día disponible.
 */
export function sumarMeses(fechaIso, meses, diaForzado = null) {
  const [a, m, d] = String(fechaIso).slice(0, 10).split('-').map(Number);
  const dia = diaForzado ?? d;
  const destino = new Date(a, m - 1 + meses, 1);
  const ultimo = new Date(destino.getFullYear(), destino.getMonth() + 1, 0).getDate();
  destino.setDate(Math.min(dia, ultimo));
  return iso(destino);
}

export function diasEntre(desdeIso, hastaIso) {
  const a = new Date(String(desdeIso).slice(0, 10) + 'T12:00:00');
  const b = new Date(String(hastaIso).slice(0, 10) + 'T12:00:00');
  return Math.round((b - a) / DIA);
}

/**
 * En qué factura cae una compra con tarjeta, y hasta cuándo hay para pagarla.
 *
 * Esta es la función que convierte "compré hoy" en una fecha real de pago.
 * Devuelve también los días de financiación, que es el dato que hace pensar
 * antes de comprar: son días de plata ajena, gratis, si se paga completo.
 */
export function facturaDe(fechaCompra, { dia_corte, dia_limite_pago }) {
  const [a, m, d] = String(fechaCompra).slice(0, 10).split('-').map(Number);

  // Comprar EL día del corte o antes entra en la factura de este periodo.
  // Comprar después se va a la siguiente: casi un mes más de plazo.
  const mesesAlCorte = d > dia_corte ? 1 : 0;
  const baseCorte = new Date(a, m - 1 + mesesAlCorte, 1);
  const ultimoCorte = new Date(baseCorte.getFullYear(), baseCorte.getMonth() + 1, 0).getDate();
  baseCorte.setDate(Math.min(dia_corte, ultimoCorte));
  const corte = iso(baseCorte);

  // La fecha límite cae DESPUÉS del corte. Si el día es menor o igual,
  // es del mes siguiente (corte el 25, pago el 5 → el 5 del mes que viene).
  const mesesAlLimite = dia_limite_pago <= dia_corte ? 1 : 0;
  const limite = sumarMeses(corte, mesesAlLimite, dia_limite_pago);

  return {
    corte,
    limite,
    diasDeFinanciacion: diasEntre(fechaCompra, limite),
  };
}

// ─── El plan de cuotas ──────────────────────────────────────────────────────

/**
 * Genera el calendario completo de una compra diferida.
 *
 * @param {object} p
 * @param {number} p.monto          entero, en pesos, lo que costó
 * @param {number} p.cuotas         en cuántas se difirió
 * @param {string} p.fechaCompra    'YYYY-MM-DD'
 * @param {number|null} p.tasaEa    % efectivo anual. null o 0 = promoción sin intereses
 * @param {object|null} p.tarjeta   { dia_corte, dia_limite_pago } si es tarjeta
 * @param {string|null} p.primeraCuota  fecha explícita de la primera cuota
 * @param {number|null} p.diaPago   día del mes de cada cuota (crédito por compra)
 * @param {string|null} p.sistema   'frances' | 'capital_fijo'. Si no se dice,
 *                                  la tarjeta es capital fijo y lo demás francés.
 *
 * @returns {Array<{numero, fecha, valor, capital, interes, saldo_despues}>}
 */
export function planDeCuotas({
  monto,
  cuotas = 1,
  fechaCompra,
  tasaEa = null,
  tarjeta = null,
  primeraCuota = null,
  diaPago = null,
  sistema = null,
}) {
  const total = Math.round(Math.abs(monto));
  const n = Math.max(1, Math.round(cuotas));
  if (!total) return [];

  // ── Cuándo cae cada cuota ──
  let fechas;
  if (primeraCuota) {
    const dia = diaPago ?? Number(String(primeraCuota).slice(8, 10));
    fechas = Array.from({ length: n }, (_, i) => sumarMeses(primeraCuota, i, dia));
  } else if (tarjeta?.dia_corte) {
    // En tarjeta, la primera cuota se paga en la factura donde cayó la compra.
    const { limite } = facturaDe(fechaCompra, tarjeta);
    fechas = Array.from({ length: n }, (_, i) =>
      sumarMeses(limite, i, tarjeta.dia_limite_pago));
  } else {
    // Crédito por compra: la primera cuota es a un mes del día que compraste,
    // que es como cobra Addi salvo que uno elija otro día.
    const dia = diaPago ?? Number(String(fechaCompra).slice(8, 10));
    fechas = Array.from({ length: n }, (_, i) => sumarMeses(fechaCompra, i + 1, dia));
  }

  // ── Cuánto vale cada cuota ──
  const sinIntereses = !tasaEa || tasaEa <= 0 || n === 1;

  if (sinIntereses) {
    // Reparto exacto: los pesos sobrantes se distribuyen, no se redondean.
    const partes = repartir(total, n);
    let saldo = total;
    return partes.map((valor, i) => {
      saldo -= valor;
      return {
        numero: i + 1,
        fecha: fechas[i],
        valor,
        capital: valor,
        interes: 0,
        saldo_despues: saldo,
      };
    });
  }

  const i = eaAMv(tasaEa) / 100;

  if ((sistema ?? (tarjeta?.dia_corte ? 'capital_fijo' : 'frances')) === 'capital_fijo') {
    // La compra se reparte exacta (como sin intereses) y encima de cada
    // parte van los intereses de lo que se sigue debiendo ese mes.
    //
    // El primer mes no es un mes entero: en tarjeta, el interés corre desde
    // el día de la compra hasta el corte (Nu: "se aplican intereses desde la
    // fecha de compra"). Comprar el 5 con corte el 28 son 23 días, no 30.
    const partes = repartir(total, n);
    const diasPrimerPeriodo = tarjeta?.dia_corte
      ? Math.max(0, diasEntre(fechaCompra, facturaDe(fechaCompra, tarjeta).corte))
      : 30;
    let saldo = total;
    return partes.map((capital, k) => {
      const factor = k === 0 ? (1 + i) ** (diasPrimerPeriodo / 30) - 1 : i;
      const interes = Math.round(saldo * factor);
      saldo -= capital;
      return {
        numero: k + 1,
        fecha: fechas[k],
        valor: capital + interes,
        capital,
        interes,
        saldo_despues: saldo,
      };
    });
  }

  // Amortización francesa: cuota fija, y dentro de ella cambia la mezcla.
  // Al principio casi todo es interés; al final casi todo es capital.
  //
  //            i
  //   C = P ───────────────
  //          1 − (1+i)^−n
  const cuotaFija = Math.round((total * i) / (1 - (1 + i) ** -n));

  const filas = [];
  let saldo = total;
  for (let k = 0; k < n; k++) {
    const interes = Math.round(saldo * i);
    const ultima = k === n - 1;

    // La última cuota liquida el saldo exacto. Sin esto quedan pesos vivos
    // después de pagar todo, o la deuda cierra en negativo.
    const capital = ultima ? saldo : Math.min(saldo, cuotaFija - interes);
    const valor = capital + interes;
    saldo -= capital;

    filas.push({
      numero: k + 1,
      fecha: fechas[k],
      valor,
      capital,
      interes,
      saldo_despues: saldo,
    });
  }
  return filas;
}

/** Lo que cuesta de más haber diferido, en pesos contantes. */
export function costoDeDiferir(plan) {
  return plan.reduce((t, c) => t + c.interes, 0);
}

// ─── Pagar ──────────────────────────────────────────────────────────────────

/**
 * Reparte los abonos sobre el plan, del más viejo al más nuevo.
 *
 * Así es como se imputan los pagos en la vida real: lo que abonas tapa
 * primero lo más atrasado. Un abono puede cubrir media cuota, una cuota y
 * media, o tres cuotas de un solo golpe; esta función no pregunta, reparte.
 */
export function aplicarAbonos(plan, abonos = []) {
  const pagado = abonos.reduce((t, a) => t + Math.round(a.monto || 0), 0);
  let restante = pagado;

  const filas = plan.map((c) => {
    const aplicado = Math.max(0, Math.min(restante, c.valor));
    restante -= aplicado;
    return {
      ...c,
      abonado: aplicado,
      pendiente: c.valor - aplicado,
      pagada: aplicado >= c.valor,
    };
  });

  return { filas, pagado, sobrante: restante };
}

/**
 * El estado de una compra diferida hoy: lo único que uno quiere ver de un
 * vistazo. Cuánto llevas, cuánto falta, cuál sigue y si estás en mora.
 */
export function estadoPlan(plan, abonos = [], hoy = hoyISO()) {
  const { filas, pagado, sobrante } = aplicarAbonos(plan, abonos);
  const total = plan.reduce((t, c) => t + c.valor, 0);

  const pendientes = filas.filter((c) => !c.pagada);
  const vencidas = pendientes.filter((c) => c.fecha < hoy);
  const proxima = pendientes[0] ?? null;

  return {
    total,
    pagado: Math.min(pagado, total),
    pendiente: Math.max(0, total - pagado),
    sobrante,                                   // pagó de más
    cuotasTotales: plan.length,
    cuotasPagadas: filas.filter((c) => c.pagada).length,
    proxima,
    diasParaProxima: proxima ? diasEntre(hoy, proxima.fecha) : null,
    vencidas,
    enMora: vencidas.length > 0,
    diasMora: vencidas.length ? diasEntre(vencidas[0].fecha, hoy) : 0,
    saldada: pagado >= total,
    filas,
  };
}

/**
 * Cuánto hay que pagar HOY para no deber nada más, y cuánto se ahorra.
 *
 * ── LEY 1555 DE 2012 ────────────────────────────────────────────────────────
 *
 * En Colombia uno tiene DERECHO a prepagar un crédito, total o parcialmente,
 * en cualquier momento y sin penalidad ni sanción. El pago se abona a capital
 * y los intereses se recalculan sobre el saldo que queda. Lo dice la ley, no
 * es un favor del banco, y aplica a los créditos de menos de 880 salarios
 * mínimos. Si alguien cobra por prepagar, está cobrando algo ilegal.
 *
 * Por eso liquidar anticipado no cuesta las cuotas que faltan: cuesta el
 * CAPITAL que falta. La diferencia es interés que nunca se causa.
 *
 * Nota honesta: la entidad puede cobrar además los intereses corridos entre
 * la última cuota y el día del prepago. Por eso el número que se muestra va
 * como "aproximadamente" y se aclara que hay que confirmarlo con la entidad.
 */
export function liquidarHoy(plan, abonos = [], hoy = hoyISO()) {
  const { filas } = aplicarAbonos(plan, abonos);
  const pendientes = filas.filter((c) => !c.pagada);
  if (!pendientes.length) {
    return { capital: 0, siPagaCuotas: 0, ahorro: 0, cuotasQueEvita: 0 };
  }

  // Capital pendiente: la parte de cada cuota que es deuda de verdad.
  // En la cuota a medio pagar, lo abonado tapa primero el interés.
  let capital = 0;
  for (const c of pendientes) {
    const cubierto = c.abonado;
    const interesCubierto = Math.min(cubierto, c.interes);
    capital += c.capital - (cubierto - interesCubierto);
  }

  const siPagaCuotas = pendientes.reduce((t, c) => t + c.pendiente, 0);

  return {
    capital: Math.max(0, capital),
    siPagaCuotas,
    ahorro: Math.max(0, siPagaCuotas - capital),
    cuotasQueEvita: pendientes.length,
    hoy,
  };
}

// ─── Mirar todas las compras juntas ─────────────────────────────────────────

/**
 * Lo que hay que pagar en un mes, sumando TODAS las compras diferidas.
 *
 * Es el número que de verdad importa y que nadie tiene: no "debo $1.200.000
 * en Addi", sino "este mes me toca soltar $310.000 entre todas las cuotas".
 * Uno no paga el total, paga las cuotas del mes.
 */
export function cuotasDelMes(compras, mes = hoyISO().slice(0, 7)) {
  const filas = [];

  for (const c of compras) {
    if (c.borrado_en || c.pagada) continue;
    const plan = c.plan?.length ? c.plan : planDeCuotas(paramsDe(c));
    const { filas: conAbonos } = aplicarAbonos(plan, c.abonos ?? []);

    for (const cuota of conAbonos) {
      if (cuota.pagada || !cuota.fecha.startsWith(mes)) continue;
      filas.push({
        compra_id: c.id,
        descripcion: c.descripcion || c.comercio || 'Compra',
        comercio: c.comercio,
        cuenta_id: c.cuenta_id,
        numero: cuota.numero,
        de: plan.length,
        fecha: cuota.fecha,
        valor: cuota.pendiente,
      });
    }
  }

  filas.sort((a, b) => a.fecha.localeCompare(b.fecha));
  return { filas, total: filas.reduce((t, f) => t + f.valor, 0) };
}

/** Los parámetros de `planDeCuotas` a partir de una fila de compras_diferidas. */
export function paramsDe(compra, tarjeta = null) {
  return {
    monto: compra.monto_total,
    cuotas: compra.cuotas_totales,
    fechaCompra: compra.fecha_compra,
    tasaEa: compra.sin_intereses ? null : compra.tasa_ea,
    tarjeta,
    primeraCuota: compra.fecha_primera_cuota,
    diaPago: compra.dia_pago,
    sistema: compra.sistema_amortizacion ?? null,
  };
}

// ─── Lo que el banco se lleva solo ──────────────────────────────────────────

/**
 * Los descuentos que el banco ya debió hacer solo, por las cuotas vencidas.
 *
 * ── CÓMO LO HACE NEQUI (lo contó Esteban, y lo muestra su extracto) ─────────
 *
 * "Él lo descuenta si tengo plata, y me deja en cero. Luego descuenta lo que
 * faltó cuando tenga." El 2 de septiembre la cuota era de $99.684 y en Nequi
 * había $92.094: se llevó eso. El 3 le entraron $550.000 y se llevó los $7.590
 * que faltaban. Dos descuentos para una cuota.
 *
 * Esta función hace lo mismo sobre los movimientos anotados: el día de la
 * cuota toma lo que haya en la cuenta; si no alcanza, cada día que la cuenta
 * vuelva a tener plata toma lo que falte. Nunca deja la cuenta en negativo y
 * nunca toma más de lo que se debe.
 *
 * Es pura: no guarda nada. Quien la llama decide qué anotar y con qué id.
 *
 * @param {object} p
 * @param {Array}  p.plan            el calendario (planDeCuotas)
 * @param {Array}  p.abonos          lo ya pagado de esta compra
 * @param {Array}  p.movimientos     los de la cuenta que paga (entran y salen)
 * @param {string} p.cuentaId        la cuenta que paga
 * @param {number} p.saldoInicial    su saldo inicial
 * @param {string} p.hoy
 * @returns {Array<{fecha, monto, interes, cubre, pagadoAntes}>}
 */
export function debitosAutomaticos({
  plan, abonos = [], movimientos = [], cuentaId, saldoInicial = 0, hoy = hoyISO(),
}) {
  const { filas, pagado } = aplicarAbonos(plan, abonos);
  const vencidas = filas.filter((c) => !c.pagada && c.fecha <= hoy);
  if (!vencidas.length) return [];

  // El saldo de la cuenta al final de cada día, con todo lo anotado.
  const delta = new Map();
  let antes = saldoInicial;
  const primera = vencidas[0].fecha;
  for (const m of movimientos) {
    if (m.borrado_en || !m.fecha) continue;
    const signo = (m.cuenta_destino === cuentaId ? 1 : 0) - (m.cuenta_origen === cuentaId ? 1 : 0);
    if (!signo) continue;
    if (m.fecha < primera) antes += signo * m.monto;
    else if (m.fecha <= hoy) delta.set(m.fecha, (delta.get(m.fecha) ?? 0) + signo * m.monto);
  }
  // Los días que importan: cada vencimiento, y cada día que la cuenta se movió.
  const dias = [...new Set([...vencidas.map((c) => c.fecha), ...delta.keys()])].sort();

  // Lo que se debe, cuota por cuota, en el orden en que se cobra.
  const cola = vencidas.map((c) => ({ ...c }));
  const debitos = [];
  let saldo = antes;
  let tomado = 0;
  let pagadoAntes = pagado;

  for (const dia of dias) {
    saldo += delta.get(dia) ?? 0;
    // Solo cuenta lo que ya estaba vencido ese día.
    const exigible = cola.filter((c) => c.pendiente > 0 && c.fecha <= dia);
    if (!exigible.length) continue;
    const hay = saldo - tomado;
    if (hay <= 0) continue;

    let disponible = hay;
    let monto = 0;
    let interes = 0;
    const cubre = [];
    for (const c of exigible) {
      if (disponible <= 0) break;
      const toma = Math.min(disponible, c.pendiente);
      // Lo que se abona tapa primero el interés de la cuota, como en el banco.
      const interesQueFalta = Math.max(0, c.interes - c.abonado);
      interes += Math.min(toma, interesQueFalta);
      c.abonado += toma;
      c.pendiente -= toma;
      disponible -= toma;
      monto += toma;
      cubre.push(c.numero);
    }
    if (monto <= 0) continue;
    debitos.push({ fecha: dia, monto, interes, cubre, pagadoAntes });
    tomado += monto;
    pagadoAntes += monto;
    if (cola.every((c) => c.pendiente <= 0)) break;
  }
  return debitos;
}

/**
 * La factura de una tarjeta: qué cuotas de qué compras se pagan en una fecha.
 *
 * El banco no cobra "cuota 1 de Temu, cuota 1 de Temu, cuota 1 de Temu…":
 * cobra UNA factura con todo lo del periodo. Esteban tiene 11 compras en Nu;
 * pagarlas una por una serían 11 pagos que en el extracto del banco son uno.
 *
 * @param {Array} compras  compras de UNA tarjeta, cada una con `plan` y `abonos`
 * @param {string} hoy
 * @returns {{ limite, total, capital, interes, filas: [{compra, cuotas, valor}] } | null}
 *   `limite`: la próxima fecha de pago (la más cercana con algo pendiente,
 *   o la más vieja que se pasó). `total`: todo lo que vence hasta ese día.
 */
export function facturaPendiente(compras, hoy = hoyISO()) {
  const pendientesPorCompra = compras
    .filter((c) => !c.borrado_en && !c.pagada)
    .map((c) => ({ c, filas: aplicarAbonos(c.plan, c.abonos ?? []).filas.filter((f) => !f.pagada) }))
    .filter((x) => x.filas.length);
  if (!pendientesPorCompra.length) return null;

  // Lo atrasado se paga con la próxima factura: se suma a ella.
  const fechas = pendientesPorCompra.flatMap((x) => x.filas.map((f) => f.fecha)).sort();
  const proximaFutura = fechas.find((f) => f >= hoy);
  const limite = proximaFutura ?? fechas[fechas.length - 1];

  const filas = [];
  for (const { c, filas: fs } of pendientesPorCompra) {
    const delPeriodo = fs.filter((f) => f.fecha <= limite);
    if (!delPeriodo.length) continue;
    const valor = delPeriodo.reduce((t, f) => t + f.pendiente, 0);
    const interes = delPeriodo.reduce((t, f) => t + Math.max(0, f.interes - f.abonado), 0);
    filas.push({ compra: c, cuotas: delPeriodo.map((f) => f.numero), valor, interes });
  }
  const total = filas.reduce((t, f) => t + f.valor, 0);
  const interes = filas.reduce((t, f) => t + f.interes, 0);
  return { limite, total, interes, capital: total - interes, filas };
}

/**
 * Reparte UN pago de la tarjeta entre sus compras.
 *
 * Primero todo lo que vence hasta la fecha de la factura, de la compra más
 * vieja a la más nueva. Si sobra, se abona a las cuotas siguientes, en el
 * mismo orden: eso es prepagar, y por ley no tiene penalidad.
 *
 * @param {Array}  compras  de UNA tarjeta, cada una con `plan` y `abonos`
 * @param {number} monto    lo que se pagó
 * @param {string} limite   la fecha de la factura que se está pagando
 * @returns {{ partes: [{compra, monto, interes, cubre}], sobrante }}
 */
export function repartirPagoTarjeta(compras, monto, limite) {
  const vivas = compras
    .filter((c) => !c.borrado_en && !c.pagada)
    .sort((a, b) => String(a.fecha_compra).localeCompare(String(b.fecha_compra)) ||
                    String(a.id).localeCompare(String(b.id)))
    .map((c) => ({ c, filas: aplicarAbonos(c.plan, c.abonos ?? []).filas.map((f) => ({ ...f })) }));

  const partes = new Map();
  let queda = Math.round(monto);

  const tomar = (x, f) => {
    const toma = Math.min(queda, f.pendiente);
    if (toma <= 0) return;
    const interes = Math.min(toma, Math.max(0, f.interes - f.abonado));
    f.abonado += toma;
    f.pendiente -= toma;
    queda -= toma;
    const p = partes.get(x.c.id) ?? { compra: x.c, monto: 0, interes: 0, cubre: [] };
    p.monto += toma;
    p.interes += interes;
    p.cubre.push(f.numero);
    partes.set(x.c.id, p);
  };

  // 1. Lo de la factura.
  for (const x of vivas) {
    for (const f of x.filas) {
      if (queda <= 0) break;
      if (f.pendiente > 0 && f.fecha <= limite) tomar(x, f);
    }
  }
  // 2. Lo que sobre, a las cuotas que siguen.
  for (const x of vivas) {
    for (const f of x.filas) {
      if (queda <= 0) break;
      if (f.pendiente > 0) tomar(x, f);
    }
  }
  return { partes: [...partes.values()], sobrante: queda };
}
